'use client';

import * as React from 'react';
import { cn } from '../../utils/cn/cn';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  hasAutoSeparation?: boolean;
  isActive?: boolean;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  hasDivider?: boolean;
}
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  hasDivider?: boolean;
}
const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, hasAutoSeparation, isActive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'bg-color-background-base rounded shadow-xs transition-all',
        hasAutoSeparation && 'divide-color-grey divide-y',
        isActive && 'border-l-color-primary border-l-4',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, hasDivider, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex gap-2 px-2 py-1', hasDivider && 'border-color-grey border-b', className)}
      {...props}
    />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-size-headline-base', className)} {...props} />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardSubtitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-size-body-sm text-color-text-darktransparent mt-0.5', className)}
    {...props}
  />
));
CardSubtitle.displayName = 'CardSubtitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-size-body-xs mt-0.5', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-2 pt-2', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ hasDivider, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center px-2',
        hasDivider && 'border-color-grey border-t',
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

export interface CardActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: string;
}

const CardAction = React.forwardRef<HTMLButtonElement, CardActionProps>(
  ({ className, icon, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn('text-color-text-dark flex items-center gap-2 transition-colors', className)}
      {...props}
    >
      {icon && <span className={cn(icon, 'size-3')} />}
      <span>{children}</span>
    </button>
  ),
);
CardAction.displayName = 'CardAction';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardSubtitle,
  CardDescription,
  CardContent,
  CardAction,
};
